import { BaseViewStore } from 'core/stores';
import { action, observable, computed, makeObservable, runInAction } from 'mobx';
import {
	confirmShipmentStatus,
	conversionRates,
	lookupRouteName,
	orderTypes,
	roles,
	shipmentDocumentSourceEnum,
	shipmentDocumentTypeEnum,
	shipmentInfoStatusEnum,
	sourceOfChange,
} from 'common/constants';
import { onUploadBOLStatuses, onUploadLabelStatuses } from 'common/constants/fulfillmentOrderConstants';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import FulfillmentOrderProcessingStore from './FulfillmentOrderProcessingStore';
import OrderStore from './OrderStore';
import ShipmentDocumentStore from './ShipmentDocumentStore';
import { PackingSlipStore } from 'modules/packing-slip/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import {
	onPrintLabelStatuses,
	onPrintPackingSlipStatuses,
	fulfillmentOrderStatus,
	channelType,
} from 'common/constants';
import {
	notificationError,
	isSeller,
	isSome,
	handleVoidLabelResponseNotification,
	translate,
	isSmartshipWarehouseAdministrator,
} from 'core/utils';
import { ModalParams } from 'core/models';
import { createFileUrl, isSuperAdminOrganization } from 'core/utils';
import moment from 'moment';
import _ from 'lodash';
import {
	AssignCarrierAccount,
	AssignCarrierServiceAndWarehouse,
	ChangeShipping,
	ConfirmShipmentErrorModal,
	EditOrder,
	MarkAsShipped,
	NeverShipped,
	OnHold,
	SplitFulfillmentOrder,
	UploadDocument,
	VoidLabel,
} from '../pages';
import isSellerCloudVendorOrder from 'core/utils/isSellerCloudVendorOrder';
import { BillOfLadingStore } from 'modules/bill-of-lading/stores';
import Cancel from '../pages/Cancel';
import { onHoldReason } from 'common/constants';
import { BaasicDropdownViewStore, ImageViewStore } from 'common/stores';
import { testImage } from 'images/index';
import { LookupStore } from 'modules/lookup/stores';
import { EditDateForm } from '../forms';
import printJS from 'print-js';

class OrderDetailViewStore extends BaseViewStore {
	@observable canCancel = false;
	@observable fulfillmentOrder = null;
	@observable activeShipmentInfo = null;
	@observable actionMenuOpen = false;
	@observable shouldDisplayActionButton = false;
	@observable hasLabel = false;
	@observable hasBOL = false;
	@observable hasUpcLabel = false;
	@observable hasGs1Label = false;
	@observable selectedItem;
	@observable editFulfillmentOrderSplit = false;
	@observable addressValidationErrorsTooltipData = '';
	@observable editableOrderType = null;
	@observable editOrderTypeEnabled = false;
	@observable relatedFulfillmentOrders = null;
	@observable editWhShipByDateEnabled = false;
	@observable errorLogClass = null;
	@observable isUpdateWhShipByDateInProgress = false;
	@observable isUpdateOrderTypeInProgress = false;
	editableWhShipByDateForm = new EditDateForm();
	reload = false;
	openPrintModal = false;
	pdfURL = null;
	isPrintingPackingSlipAllowed = null;
	fulfillmentOrderItems = null;
	itemPrice = 0;
	order = null;
	hasActiveShipment = false;
	fulfillmentOrderStatus;
	packingSlipStore = null;
	isBuyShipping = false;
	showVoidOnChannel = false;
	isMultiItem = false;
	isReentered = false;
	orderAddressData = null;
	dimensionUnit = null;
	weightUnit = null;
	pallets = null;
	shipmentDimensions = null;
	palletDimensions = [];
	@observable confirmShipmentInProgress = false;
	@observable confirmShipmentFailed = false;
	@observable proNumber = null;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.packingSlipStore = new PackingSlipStore(rootStore);
		this.billOfLadingStore = new BillOfLadingStore(rootStore);
		this.shipmentDocumentStore = new ShipmentDocumentStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.orderStore = new OrderStore(rootStore);
		this.rootStore.viewStore.modes = [];
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isSmartshipWarehouseAdministrator = isSmartshipWarehouseAdministrator(rootStore);
		this.isSeller = isSeller(rootStore);
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.isWarehouseAdministrator =
			_.findIndex(
				rootStore.app.baasic.userHandler.user.user.roles,
				role => role === roles.warehouseAccountOwner || role === roles.warehouseAdministrator
			) != -1;
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		this.fulfillmentOrderEmbeds = [
			'shippingCoreCountry',
			'shippingCoreCountryState',
			'carrier',
			'carrierService',
			'carrierService.carrierType',
			'cancelReason',
			'originalFulfillmentOrder',
			'seller',
			'seller.sellerOrganizationInfo',
			'warehouseOrganization',
			'warehouseOrganization.convertWarehouses',
			'warehouseOrganization.warehouseOrganizationInfo',
			'warehouseOrganization.warehouseOrganizationInfo.address',
			'warehouseOrganization.warehouseOrganizationInfo.contact',
			'warehouseOrganization.warehouseOrganizationInfo.address.coreCountry',
			'warehouseOrganization.warehouseOrganizationInfo.address.coreCountryState',
			'onHoldReason',
			'replacementReason',
			'fulfillmentOrderItems',
			'fulfillmentOrderItems.orderLine',
			'fulfillmentOrderItems.orderLine.product',
			'fulfillmentOrderItems.product',
			'fulfillmentOrderItems.product.dimensionUnit',
			'fulfillmentOrderItems.product.weightUnit',
			'shipmentInfos',
			'shipmentInfos.pallet',
			'fulfillmentOrderStatus',
			'fulfillmentOrderErrors',
			'order',
			'order.orderLines',
			'order.shippingCoreCountry',
			'order.shippingCoreCountryState',
			'order.confirmationType',
			'order.carrier',
			'order.channel',
			'order.channel.channelType',
			'order.channel.timezone',
			'order.orderStatus',
			'order.orderType',
			'order.shippingCoreCountry',
			'order.shippingCoreCountryState',
			'order.requiredCarrier',
			'fulfillmentOrderImages',
			'confirmShipmentStatus',
		];
		await this.getFulfillmentOrder(this.rootStore.routerStore.routerState.params.id, {
			embed: this.fulfillmentOrderEmbeds,
		});
		this.relatedFulfillmentOrders = await this.fulfillmentOrderStore.getRelatedFulfillmentOrders(
			this.fulfillmentOrder.id
		);
		this.billOfLadingSource = translate('ORDERVIEW.INTERNALBOL');
		if (_.get(this.fulfillmentOrder, 'billOfLading')) {
			for (const [key, obj] of Object.entries(shipmentDocumentSourceEnum)) {
				if (obj.value === this.fulfillmentOrder.billOfLading.source) {
					this.billOfLadingSource = obj.label;
				}
			}
		}
		if (this.openPrintModal) {
			this.openPrintModal = false;
			setTimeout(function() {
				const frame = window.frames['pdf-print'];
				frame.print();
			}, 250);
		}
		this.calculateItemPrice();
		this.order = this.fulfillmentOrder.order;
		if (this.fulfillmentOrder.addressValidationErrors && this.fulfillmentOrder.addressValidationErrors !== '') {
			this.addressValidationErrorsTooltipData = this.fulfillmentOrder.addressValidationErrors.split(',');
		} else {
			this.addressValidationErrorsTooltipData = '';
		}
		this.orderTypeDropdownStore = new BaasicDropdownViewStore({
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.orderType,
		});
		var orderType = this.fulfillmentOrder.order.orderType;
		this.orderTypeDropdownStore.setValue(orderType);
		if (isSome(this.fulfillmentOrder.whShipByDate)) {
			this.editableWhShipByDateForm
				.$('date')
				.set('value', moment(this.fulfillmentOrder.whShipByDate).format('MM-DD-YYYY'));
		}
		if (this.fulfillmentOrder.hasErrors) {
			this.errorLogClass = 'fa fa-exclamation-circle spc--right--tny k-custom-clickable';
		} else {
			this.errorLogClass = 'fa fa-check-circle spc--right--tny k-custom-clickable';
		}
		await this.checkConfirmShipmentFailed();
		this.resume();
	};

	@action.bound
	getWarehouseCodesForDisplay(item) {
		let code = _.find(item.warehouseOrganization.convertWarehouses, convertWarehouse => {
			return (
				convertWarehouse.channelId == item.order.channelId && convertWarehouse.warehouseId == item.assignedWarehouseId
			);
		});

		if (isSome(code)) {
			return code.name;
		} else {
			return '-';
		}
	}

	@action.bound
	showFulfillmentOrderImages = () => {
		this.fulfillmentOrderImageViewStore.openModal();
	};

	@action.bound
	getFulfillmentOrder = async (id, options) => {
		await this.fulfillmentOrderStore.getFulfillmentOrder(id, options);
		this.fulfillmentOrder = this.fulfillmentOrderStore.item;
		this.fulfillmentOrderItems = this.fulfillmentOrder.fulfillmentOrderItems;
		this.selectedItem = this.fulfillmentOrder;
		//If there is any address data on FO show those data
		this.orderAddressData = this.fulfillmentOrder;
		this.hasLabel = _.find(this.fulfillmentOrder.shipmentInfos, item => {
			return (
				(isSome(item.label) && item.status == shipmentInfoStatusEnum.active) ||
				((this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.shippedExternally ||
					this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan) &&
					item.status == shipmentInfoStatusEnum.active)
			);
		});
		this.hasBOL = _.find(this.fulfillmentOrder.shipmentInfos, item => {
			return (
				(isSome(item.BOL) && item.status == shipmentInfoStatusEnum.active) ||
				((this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.shippedExternally ||
					this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan) &&
					item.status == shipmentInfoStatusEnum.active)
			);
		});
		this.hasUpcLabel = _.get(this.fulfillmentOrder, 'upcLabel') != null;
		this.hasGs1Label = this.fulfillmentOrder.hasGs1Label === true;
		this.isBoxRequired =
			this.fulfillmentOrder.fulfillmentOrderItems.length > 1 ||
			(this.fulfillmentOrder.fulfillmentOrderItems[0] && this.fulfillmentOrder.fulfillmentOrderItems[0].quantity > 1);
		this.isVendorOrder =
			this.fulfillmentOrder.order.channel.channelType.name === channelType.amazonVendor ||
			isSellerCloudVendorOrder(this.fulfillmentOrder.order);
		this.isLtlOrder =
			this.selectedItem.order.channel.name === 'LTL Freight' || this.selectedItem.order.requiredCarrierService == 'LTL';
		// Backend flag: true for a designated LTL order OR one that won an LTL rate (a Priority1 quote id is
		// stored). Unlike isLtlOrder (channel-only), this catches priceable orders assigned an LTL rate off a
		// regular channel, which have no shipping document until dispatch happens in LTL packing mode.
		this.isLtlShipment = this.fulfillmentOrder.isLtlShipment === true;
		this.isExternalOrder = _.get(this.fulfillmentOrder, 'order.orderType.name') === orderTypes.externalOrder;

		this.editFulfillmentOrderSplit =
			(this.fulfillmentOrder.isSplit ||
				(this.fulfillmentOrder.originalFulfillmentOrder != null &&
					this.fulfillmentOrder.originalFulfillmentOrder.isSplit)) &&
			!this.hasLabel;

		// hasPackingSlip is resolved by the API: a stored packing slip document, or an Amazon Vendor channel,
		// whose slips are fetched from Amazon per print. Nothing else is printable since the generated slip went away.
		this.isPrintingPackingSlipAllowed =
			this.fulfillmentOrder.hasPackingSlip === true &&
			_.findIndex(onPrintPackingSlipStatuses, item => item == this.fulfillmentOrder.fulfillmentOrderStatus.abrv) != -1;

		const isPrintingAllowed =
			_.findIndex(onPrintLabelStatuses, item => item == this.fulfillmentOrder.fulfillmentOrderStatus.abrv) != -1;
		if (
			// Used for rendering Tracking Number and Is Buy Shipping flag in the Header. Reprint is not available.
			this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.replaced ||
			this.fulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.reassigned
		) {
			this.activeShipmentInfo = _.find(this.fulfillmentOrder.shipmentInfos, item => {
				return item.status == shipmentInfoStatusEnum.active;
			});
		}
		if (isPrintingAllowed) {
			this.activeShipmentInfo = _.find(this.fulfillmentOrder.shipmentInfos, item => {
				return item.status == shipmentInfoStatusEnum.active;
			});
			if (this.activeShipmentInfo != null && this.activeShipmentInfo.hasOwnProperty('label')) {
				if (this.activeShipmentInfo.label != null) {
					this.pdfURL = createFileUrl(this.activeShipmentInfo.label);
				}
			}
		} else {
			this.pdfURL = null;
		}
		this.getLocale();
		if (
			this.fulfillmentOrder.onHoldReason != null &&
			this.fulfillmentOrder.onHoldReason.abrv == 'to%20be%20cancelled'
		) {
			this.canCancel = true;
		} else {
			this.canCancel = false;
		}
		this.hasActiveShipment = this.activeShipmentInfo != null;
		this.fulfillmentOrderStatus = this.selectedItem.fulfillmentOrderStatus.abrv;
		this.isBuyShipping = this.activeShipmentInfo != null && this.activeShipmentInfo.isBuyShipping;
		this.isReentered = this.fulfillmentOrder.newShipByDate >= this.fulfillmentOrder.shipByDate;
		this.dimensionUnit = _.get(this.fulfillmentOrder, 'fulfillmentOrderItems[0].product.dimensionUnit.name');
		this.weightUnit = _.get(this.fulfillmentOrder, 'fulfillmentOrderItems[0].product.weightUnit.name');
		this.pallets = _.get(this.fulfillmentOrder, 'pallets');
		this.setShipmentDimensions();

		if (this.selectedItem.fulfillmentOrderImages && this.selectedItem.fulfillmentOrderImages.length > 0) {
			const latestImage = _.orderBy(this.selectedItem.fulfillmentOrderImages, ['dateCreated', ['desc']])[0];
			if (latestImage) {
				this.fulfillmentOrderImageViewStore = new ImageViewStore({
					images:
						process.env.NODE_ENV === 'production' || this.selectedItem.productionMode ? [latestImage.url] : [testImage],
				});
			}
		}
	};

	// Dimensions/weight are pulled from the shipment record (active shipment info for parcel, each pallet for LTL),
	// which is always stored imperial. We display them in the product's unit (converting when the product is metric)
	// and show a tooltip with the value in the other measurement unit.
	setShipmentDimensions = () => {
		this.shipmentDimensions =
			this.activeShipmentInfo &&
			this.activeShipmentInfo.length &&
			this.activeShipmentInfo.width &&
			this.activeShipmentInfo.height
				? this.buildDimensionInfo(
						this.activeShipmentInfo.length,
						this.activeShipmentInfo.width,
						this.activeShipmentInfo.height,
						this.activeShipmentInfo.weight
				  )
				: null;

		this.palletDimensions = (_.get(this.fulfillmentOrder, 'pallets') || []).map(p =>
			this.buildDimensionInfo(p.length, p.width, p.height, p.weight)
		);
	};

	// Builds { display, tooltip } from imperial shipment values. `display` is in the product's unit
	// (converted when metric); `tooltip` is the same value in the other measurement unit.
	buildDimensionInfo = (lengthIn, widthIn, heightIn, weightLb) => {
		const fmt = v => parseFloat(Number(v || 0).toFixed(2));
		const inToM = v => fmt(v * conversionRates.inchInMeters);
		const lbToKg = v => fmt(v * conversionRates.poundsInKilogram);
		const dimsMetric = this.dimensionUnit === 'Meters';
		const weightMetric = this.weightUnit === 'Kilograms';

		const dispDims = dimsMetric ? [lengthIn, widthIn, heightIn].map(inToM) : [lengthIn, widthIn, heightIn].map(fmt);
		const tipDims = dimsMetric ? [lengthIn, widthIn, heightIn].map(fmt) : [lengthIn, widthIn, heightIn].map(inToM);
		const dispDimUnit = dimsMetric ? 'm' : 'in';
		const tipDimUnit = dimsMetric ? 'in' : 'm';

		const hasWeight = weightLb != null && weightLb > 0;
		const dispWeight = weightMetric ? lbToKg(weightLb) : fmt(weightLb);
		const tipWeight = weightMetric ? fmt(weightLb) : lbToKg(weightLb);
		const dispWeightUnit = weightMetric ? 'kg' : 'lbs';
		const tipWeightUnit = weightMetric ? 'lbs' : 'kg';

		return {
			display: `${dispDims.join(' x ')} ${dispDimUnit}${hasWeight ? `, ${dispWeight} ${dispWeightUnit}` : ''}`,
			tooltip: `${tipDims.join(' x ')} ${tipDimUnit}${hasWeight ? `, ${tipWeight} ${tipWeightUnit}` : ''}`,
		};
	};

	@action.bound
	printLabel = async () => {
		this.openPrintModal = true;
		if (this.fulfillmentOrder.fulfillmentOrderStatus.abrv !== fulfillmentOrderStatus.exitScan) {
			await this.shipmentInfoStore.reprintLabel(this.activeShipmentInfo.id, false);
			await this.initialize();
		} else {
			this.openPrintModal = false;
			setTimeout(function() {
				const frame = window.frames['pdf-print'];
				frame.print();
			}, 250);
		}
	};

	@action.bound
	printPackingSlip = async () => {
		this.suspend();
		try {
			await this.packingSlipStore.generatePdf(this.fulfillmentOrder.id, true);
			const pdfResponse = this.packingSlipStore.response;
			if (!pdfResponse.packingSlip) {
				this.rootStore.notificationStore.warning('FULFILLMENTORDER.PACKINGSLIP.NOTFOUND');
				return;
			}
			if (!pdfResponse.printedWithPrintNode) {
				const fileURL = createFileUrl(pdfResponse.packingSlip);
				printJS(fileURL);
			}
			this.rootStore.notificationStore.success('FULFILLMENTORDER.PACKINGSLIP.SUCCESS');
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.PACKINGSLIP.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	printUpcLabel = async () => {
		this.suspend();
		try {
			const response = await this.shipmentDocumentStore.printDocument(
				this.fulfillmentOrder.id,
				shipmentDocumentTypeEnum.upcLabel
			);
			if (!response.printedWithPrintNode) {
				printJS(createFileUrl(response.content));
			}
			this.rootStore.notificationStore.success('FULFILLMENTORDER.UPCLABEL.SUCCESS');
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.UPCLABEL.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	printGs1Label = async () => {
		this.suspend();
		try {
			const response = await this.shipmentDocumentStore.printDocument(
				this.fulfillmentOrder.id,
				shipmentDocumentTypeEnum.gs1Label
			);
			if (!response.printedWithPrintNode) {
				printJS(createFileUrl(response.content));
			}
			this.rootStore.notificationStore.success('FULFILLMENTORDER.GS1LABEL.SUCCESS');
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.GS1LABEL.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	printBillOfLading = async () => {
		this.suspend();
		try {
			await this.billOfLadingStore.print(this.fulfillmentOrder.id);
			const content = this.billOfLadingStore.response;
			if (content) {
				const fileURL = createFileUrl(content);
				printJS(fileURL);
			}
			this.rootStore.notificationStore.success('FULFILLMENTORDER.BILLOFLADING.SUCCESS');
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.BILLOFLADING.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	getProNumber = async () => {
		this.suspend();
		try {
			const proNumber = await new FulfillmentOrderProcessingStore(this.rootStore).getProNumber(this.fulfillmentOrder.id);
			runInAction(() => {
				this.proNumber = proNumber;
			});
			this.rootStore.notificationStore.success(
				proNumber ? 'FULFILLMENTORDER.PRONUMBER.SUCCESS' : 'FULFILLMENTORDER.PRONUMBER.NOTAVAILABLE'
			);
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.PRONUMBER.ERROR']);
		} finally {
			this.resume();
		}
	};

	@action.bound
	calculateItemPrice() {
		let itemPrice = 0;
		_.each(this.fulfillmentOrder.order.orderLines, orderLine => {
			itemPrice += orderLine.unitPrice * orderLine.quantity;
		});
		this.itemPrice = itemPrice.toFixed(2);
	}

	@action.bound
	openVoidLabelConfirmModal = async (plainLabelVoiding, reoptimizeLabelVoiding) => {
		if (!plainLabelVoiding && this.isBuyShipping) {
			this.openConfirmationModal(
				this.getVoidLabelConfirmModalProps(reoptimizeLabelVoiding, [
					'ORDERVIEW.VOIDLABELONCHANNEL',
					{ fulfillmentOrderId: this.selectedItem.fulfillmentOrderId },
				])
			);
		} else if (plainLabelVoiding) {
			if (this.isBuyShipping) {
				this.openModal({
					onSuccess: async () => await this.initialize(),
					data: { selectedItemId: this.selectedItem.id },
					component: VoidLabel,
				});
			} else
				this.openConfirmationModal(
					this.getVoidLabelConfirmModalProps(reoptimizeLabelVoiding, [
						'ORDERVIEW.VOIDLABEL',
						{ fulfillmentOrderId: this.fulfillmentOrder.fulfillmentOrderId },
					])
				);
		} else if (reoptimizeLabelVoiding) {
			await this.reoptimizeOrder(false);
		}
	};

	openOnHoldModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id },
			component: OnHold,
		});
	};

	openAssignCarrierAccountModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id },
			component: AssignCarrierAccount,
		});
	};

	openCancelModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: {
				selectedItemId: this.selectedItem.id,
				resetPrintingData: async () => await this.resetPrintingData(),
			},
			component: Cancel,
		});
	};

	/**
	 * A shipping label is an assignment, so it is only offered while the order has none and is not LTL.
	 */
	@computed get canUploadShippingLabel() {
		return (
			(process.env.NODE_ENV === 'production' ? this.isSuperAdminOrganization || this.isSeller : true) &&
			!this.hasLabel &&
			!this.isLtlOrder &&
			!this.isLtlShipment &&
			this.rootStore.authStore.hasPiiAccess &&
			_.findIndex(onUploadLabelStatuses, status => status == _.get(this.selectedItem, 'fulfillmentOrderStatus.abrv')) != -1
		);
	}

	@computed get canUploadBillOfLading() {
		return (
			(process.env.NODE_ENV === 'production' ? this.isSuperAdminOrganization || this.isSeller : true) &&
			(this.isLtlOrder || this.isLtlShipment) &&
			_.get(this.fulfillmentOrder, 'billOfLading.source') == null &&
			this.rootStore.authStore.hasPiiAccess &&
			_.findIndex(onUploadBOLStatuses, status => status == _.get(this.selectedItem, 'fulfillmentOrderStatus.abrv')) != -1
		);
	}

	/** Packing slip, UPC label and GS1 label are plain documents - useful right up until the order is canceled. */
	@computed get canUploadDocument() {
		return _.get(this.selectedItem, 'fulfillmentOrderStatus.abrv') !== fulfillmentOrderStatus.canceled;
	}

	@computed get canUploadAnyDocument() {
		return this.canUploadShippingLabel || this.canUploadBillOfLading || this.canUploadDocument;
	}

	/**
	 * An order that won an LTL rate off a regular channel ships like any other shipment, so its BOL upload
	 * records the same details a label upload would. Designated LTL Freight orders pull their BOL from the
	 * channel and keep the document-only upload.
	 */
	@computed get billOfLadingRequiresShipmentDetails() {
		return this.isLtlShipment && !this.isLtlOrder;
	}

	openUploadDocumentModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: {
				selectedItemId: this.selectedItem.id,
				sellerId: this.selectedItem.sellerId,
				assignedCarrierId: this.selectedItem.assignedCarrierId,
				assignedCarrierServiceId: this.selectedItem.assignedCarrierServiceId,
				canUploadShippingLabel: this.canUploadShippingLabel,
				canUploadBillOfLading: this.canUploadBillOfLading,
				canUploadDocument: this.canUploadDocument,
				billOfLadingRequiresShipmentDetails: this.billOfLadingRequiresShipmentDetails,
			},
			component: UploadDocument,
		});
	};

	pullBOL = async () => {
		try {
			this.suspend();
			const response = await this.billOfLadingStore.pull(this.fulfillmentOrder.id);
			if (response) {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.PULLBOLSUCCESS',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.error([
					'ORDERVIEW.PULLBOLERROR',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			}
		} catch (error) {
			notificationError({ store: this.rootStore.notificationStore, error });
		} finally {
			await this.initialize();
			this.resume();
		}
	};

	@action.bound
	openEditOrderModal = () => {
		this.openModal(this.getEditOrderModalProps());
	};

	getEditOrderModalProps = () => {
		return {
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id, parentView: this },
			component: EditOrder,
		};
	};

	@action.bound
	resetPrintingData = async () => {
		this.activeShipmentInfo = null;
		this.pdfURL = null;
	};

	@action.bound
	cancelFulfillmentOrder = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.cancelFulfillmentOrder(
				this.fulfillmentOrder,
				sourceOfChange.cancelFulfillmentOrderList
			);
			this.rootStore.notificationStore.success([
				'ORDERDETAILVIEW.FULFILLMENTORDERCANCELED',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			]);
			this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.resume();
		}
	};

	@action.bound
	uncancelFulfillmentOrder = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.uncancelFulfillmentOrder(
				{ id: this.fulfillmentOrder.id },
				sourceOfChange.uncancelFulfillmentOrderList
			);
			this.rootStore.notificationStore.success([
				'ORDERDETAILVIEW.FULFILLMENTORDERUNCANCELED',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			]);
			this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.resume();
		}
	};

	@action.bound
	getLocale = () => {
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		// Required to be state due to the error log template which uses the store format and timezone
		this.format = localeData.longDateFormat('LLL');
		this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	};

	@action.bound
	pullLabel = async () => {
		this.suspend();
		try {
			const response = await new FulfillmentOrderProcessingStore(this.rootStore).createFulfillmentShipment(
				this.fulfillmentOrder.id,
				sourceOfChange.pullLabelOrderDetails,
				false
			);
			if (response.shipmentInfo) {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.PULLLABELSUCCESS',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			} else if (response.message || response.errors) {
				// Error notification after shipment PII data is validated as false on the backend, before create shipment request.
				this.rootStore.notificationStore.error([
					response.message ? response.message : 'ORDERVIEW.PULLLABELERROR',
					{
						id: this.fulfillmentOrder.fulfillmentOrderId,
						notification: response.errors ? response.errors.join(' ') : null,
					},
				]);
			} else if (response.shipmentData.notifications) {
				// Error notification after shipment data is validated as true, but error occured on create shipment request.
				this.rootStore.notificationStore.error([
					(response.message = 'ORDERVIEW.PULLLABELERROR'),
					{ id: this.fulfillmentOrder.fulfillmentOrderId, notification: response.shipmentData.notifications.join(' ') },
				]);
			} else {
				// Generic error notification if no specific error message has been caught.
				this.rootStore.notificationStore.error([
					'ORDERVIEW.PULLLABELERROR',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			}
		} catch (err) {
			this.rootStore.notificationStore.error([
				err.data.message ? err.data.message : 'ORDERVIEW.PULLLABELERROR',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			]);
		}
		await this.initialize();
		this.resume();
	};

	@action.bound
	updateProductionMode = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.toggleProductionMode(
				this.fulfillmentOrder.id,
				!this.fulfillmentOrder.productionMode,
				sourceOfChange.productionModeOrderDetail
			);
			if (!this.fulfillmentOrder.productionMode) {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.TOGGLEPRODUCTIONMODESUCCESSON',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.TOGGLEPRODUCTIONMODESUCCESSOFF',
					{ id: this.fulfillmentOrder.fulfillmentOrderId },
				]);
			}
			await this.initialize();
		} catch (err) {
			if (err.data != null && err.data.details != null) {
				this.rootStore.notificationStore.error(err.data.details);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	@action.bound
	voidBOL = async () => {
		try {
			this.suspend();
			await this.billOfLadingStore.void(this.fulfillmentOrder.id);
			this.rootStore.notificationStore.success([
				'VOIDBOLVIEW.SUCCESS',
				{ fulfillmentOrderId: this.fulfillmentOrder.fulfillmentOrderId },
			]);
			await this.initialize();
		} catch (error) {
			notificationError({ store: this.rootStore.notificationStore, error });
		} finally {
			this.resume();
		}
	};

	@action.bound
	reoptimizeOrder = async (voidOnChannel = false) => {
		this.suspend();
		try {
			await this.fulfillmentOrderStore.reoptimize(
				{
					fulfillmentOrderIds: [this.selectedItem.id],
					voidOnChannel: voidOnChannel,
				},
				sourceOfChange.reoptimizeOrderDetail
			);
			this.rootStore.notificationStore.success([
				'ORDERVIEW.REOPTIMIZEORDERSUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		await this.initialize();
		this.resume();
	};

	@action.bound
	displayPopoverButton = width => {
		//1165px is equal to 1440 width resolution
		if (width > 1500) {
			this.shouldDisplayActionButton = false;
		} else {
			this.shouldDisplayActionButton = true;
		}
	};

	openAssignmentModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id },
			component: AssignCarrierServiceAndWarehouse,
		});
	};

	historyLogModal = new ModalParams();

	openChangeShippingModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: {
				selectedItemId: this.selectedItem.id,
				orderTypeName: this.selectedItem.order.orderType.name,
				resetPrintingData: async () => await this.resetPrintingData(),
			},
			component: ChangeShipping,
		});
	};

	getProductionModeConfirmModalProps = checked => {
		return {
			onConfirm: async () => await this.updateProductionMode(),
			message: [
				checked ? 'ORDERVIEW.TOGGLEPRODUCTIONMODEON' : 'ORDERVIEW.TOGGLEPRODUCTIONMODEOFF',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			],
		};
	};

	getVoidBOLConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.voidBOL(),
			message: ['ORDERVIEW.VOIDBOL', { fulfillmentOrderId: this.fulfillmentOrder.fulfillmentOrderId }],
		};
	};

	getCancelConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.cancelFulfillmentOrder(),
			message: ['ORDERVIEW.CANCELFULFILLMENTORDER', { id: this.fulfillmentOrder.fulfillmentOrderId }],
		};
	};

	openNeverShippedModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItem: this.selectedItem },
			component: NeverShipped,
		});
	};

	openSplitFulfillmentOrderModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: {
				selectedItemId: this.selectedItem.id,
				orderId: this.selectedItem.orderId,
				isEdit: this.editFulfillmentOrderSplit,
			},
			component: SplitFulfillmentOrder,
			className: 'ReactModal--lrg',
		});
	};

	getReoptimizeConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.openVoidLabelConfirmModal(false, true),
			message: ['ORDERVIEW.REOPTIMIZEORDERCONFIRM', { id: this.selectedItem.fulfillmentOrderId }],
		};
	};

	getUncancelConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.uncancelFulfillmentOrder(),
			message: ['ORDERVIEW.UNCANCELFULFILLMENTORDER', { id: this.selectedItem.fulfillmentOrderId }],
		};
	};

	getVoidLabelConfirmModalProps = (reoptimizeLabelVoiding, message) => {
		return {
			onConfirm: async () => {
				this.suspend();
				try {
					if (this.rootStore.viewStore.selectedConfirmModal.data.reoptimizeLabelVoiding) {
						await this.reoptimizeOrder(true);
					} else {
						let voidLabelOption = this.activeShipmentInfo.isBuyShipping ? 1 : 0;
						const response = await this.shipmentInfoStore.voidLabel(
							{ fulfillmentOrderId: this.fulfillmentOrder.id },
							voidLabelOption,
							sourceOfChange.voidActiveLabelConfirmModal
						);

						handleVoidLabelResponseNotification(this.rootStore, response, this.fulfillmentOrder.fulfillmentOrderId);

						this.pdfURL = null;
						this.initialize();
					}
				} catch (err) {
					notificationError({ store: this.rootStore.notificationStore, err });
				}
				this.resume();
			},
			onCancel: async () => {
				if (this.rootStore.viewStore.selectedConfirmModal.data.reoptimizeLabelVoiding) {
					await this.reoptimizeOrder(false);
				}
			},
			message: message,
			data: { reoptimizeLabelVoiding: reoptimizeLabelVoiding },
		};
	};

	getRetryConfirmShipmentModalProps = () => {
		return {
			onConfirm: () => this.onConfirmShipment(),
			message: ['Retry Confirm Shipment?', { id: this.fulfillmentOrder.fulfillmentOrderId }],
		};
	};

	getPullLabelConfirmModalProps = () => {
		return {
			onConfirm: () => this.pullLabel(),
			message: [
				['staging', 'test', 'development'].includes(process.env.NODE_ENV) && this.fulfillmentOrder.productionMode
					? 'PRODUCTIONPULLLABEL'
					: 'ORDERVIEW.PULLLABELCONFIRM',
				{ id: this.fulfillmentOrder.fulfillmentOrderId },
			],
		};
	};

	getPullBOLConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.pullBOL(),
			message: ['ORDERVIEW.PULLBOLCONFIRM', { id: this.fulfillmentOrder.fulfillmentOrderId }],
		};
	};

	openMarkAsShippedModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: { selectedItemId: this.selectedItem.id },
			component: MarkAsShipped,
		});
	};

	openOrderDetailPage = orderId => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list.detail', {
			id: orderId,
		});
	};

	nonCancelHoldReason = () =>
		_.has(this.fulfillmentOrder, 'onHoldReasonAbrv')
			? this.fulfillmentOrder.onHoldReasonAbrv != onHoldReason.toBeCancelled
			: _.has(this.fulfillmentOrder, 'onHoldReason.abrv')
			? this.fulfillmentOrder.onHoldReason.abrv != onHoldReason.toBeCancelled
			: true;

	@action.bound
	onEditOrderType = () => {
		this.editOrderTypeEnabled = true;
		this.isUpdateOrderTypeInProgress = false;
	};

	@action.bound
	onCancelEditOrderType = () => {
		this.editOrderTypeEnabled = false;
		this.orderTypeDropdownStore.setValue(this.fulfillmentOrder.order.orderType);
		this.isUpdateOrderTypeInProgress = false;
	};

	@action.bound
	onEditOrderTypeSave = async () => {
		this.isUpdateOrderTypeInProgress = true;
		try {
			if (!this.orderTypeDropdownStore.value) {
				this.rootStore.notificationStore.error(['Order Type is required.']);
			} else {
				const result = await this.orderStore.updateOrderType(
					{
						orderId: this.fulfillmentOrder.orderId,
						orderTypeId: this.orderTypeDropdownStore.value.value,
					},
					'FulfillmentOrder details - Update OrderType'
				);
				if (result.statusCode === 200) {
					this.editOrderTypeEnabled = false;
					this.rootStore.notificationStore.success(
						'Order Type successfully updated for order ' + this.fulfillmentOrder.order.orderNumber
					);
					await this.getFulfillmentOrder(this.rootStore.routerStore.routerState.params.id, {
						embed: this.fulfillmentOrderEmbeds,
					});
				} else {
					this.rootStore.notificationStore.error(['Failed to update Order Type.']);
				}
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.isUpdateOrderTypeInProgress = false;
	};

	@action.bound
	onEditWhShipByDate = () => {
		this.editWhShipByDateEnabled = true;
		this.isUpdateWhShipByDateInProgress = false;
	};

	@action.bound
	onCancelEditWhShipByDate = () => {
		this.editWhShipByDateEnabled = false;
		this.editableWhShipByDateForm
			.$('date')
			.set('value', moment(this.fulfillmentOrder.whShipByDate).format('MM-DD-YYYY'));
		this.isUpdateWhShipByDateInProgress = false;
	};

	@action.bound
	onEditWhShipByDateSave = async () => {
		this.isUpdateWhShipByDateInProgress = true;
		try {
			if (this.editableWhShipByDateForm.$('date').value === '') {
				this.rootStore.notificationStore.error(['Warehouse Ship By Date is required.']);
			} else {
				if (new Date(this.editableWhShipByDateForm.$('date').value) > new Date(this.fulfillmentOrder.shipByDate)) {
					this.rootStore.notificationStore.warning([
						"Warehouse Ship By Date cannot be after Marketplace Order's Ship By Date .",
					]);
					this.isUpdateWhShipByDateInProgress = false;
					return;
				}
				this.fulfillmentOrder.whShipByDate = this.editableWhShipByDateForm.$('date').value;
				const result = await this.fulfillmentOrderStore.updateWhShipByDate(
					{
						id: this.fulfillmentOrder.id,
						newWhShipByDate: this.editableWhShipByDateForm.$('date').value,
					},
					'FulfillmentOrder Details - Edit WhShipByDate'
				);
				if (result.statusCode === 200) {
					this.editWhShipByDateEnabled = false;
					this.rootStore.notificationStore.success(
						'Warehouse Ship By Date successfully updated for order ' + this.fulfillmentOrder.fulfillmentOrderId
					);
					await this.getFulfillmentOrder(this.rootStore.routerStore.routerState.params.id, {
						embed: this.fulfillmentOrderEmbeds,
					});
				} else {
					this.rootStore.notificationStore.error(['Failed to update Warehouse Ship By Date.']);
				}
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.isUpdateWhShipByDateInProgress = false;
	};

	@action.bound
	onConfirmShipment = async () => {
		this.confirmShipmentInProgress = true;
		const { notificationStore, routerStore } = this.rootStore;
		const notificationId = notificationStore.load('Confirm Shipment in progress...');

		const handleShipmentError = (errorsList = []) => {
			notificationStore.error('Confirm Shipment failed. Click for more details.', false, {
				autoClose: false,
				onClick: () => {
					this.openModal({
						data: {
							orderId: this.fulfillmentOrder.fulfillmentOrderId,
							errorsList,
							hasErrors: true,
						},
						component: ConfirmShipmentErrorModal,
					});
				},
			});
		};

		try {
			const result = await this.fulfillmentOrderStore.confirmShipment(
				{ fulfillmentOrderId: this.fulfillmentOrder.id },
				sourceOfChange.retryConfirmShipment
			);
			notificationStore.dismiss(notificationId);
			const errors = result.data ? result.data.errors : [];
			if (result.statusCode === 200 && errors.length === 0) {
				notificationStore.success('Confirm Shipment success.');
				await this.getFulfillmentOrder(routerStore.routerState.params.id, {
					embed: this.fulfillmentOrderEmbeds,
				});
			} else {
				handleShipmentError(errors);
			}
		} catch (error) {
			notificationStore.dismiss(notificationId);
			handleShipmentError();
			console.error('Failed to confirm shipment:', error);
		} finally {
			runInAction(() => {
				this.confirmShipmentInProgress = false;
				this.checkConfirmShipmentFailed();
			});
		}
	};

	@action.bound
	checkConfirmShipmentFailed = async () => {
		if (!isSome(this.fulfillmentOrder.exitScanTimestamp)) return;
		var hour = 60 * 60 * 1000;
		var currentDateTime = Date.now();
		var exitScanTimestamp = new Date(this.fulfillmentOrder.exitScanTimestamp).getTime();
		var timeDiff = currentDateTime - exitScanTimestamp;
		var hasFailed =
			timeDiff > hour &&
			this.fulfillmentOrder.confirmShipmentStatus &&
			this.fulfillmentOrder.confirmShipmentStatus.name !== confirmShipmentStatus.confirmed;
		this.confirmShipmentFailed = hasFailed;
	};
}

export default OrderDetailViewStore;
